import {AfterViewInit, Component, TemplateRef, ViewChild} from '@angular/core';

import proj4, {TemplateCoordinates} from "proj4";
import shp from 'shpjs';
import {NgbModal, NgbNavChangeEvent} from "@ng-bootstrap/ng-bootstrap";
import {ProductService} from "../../services/product.service";
import {RegionsService} from "../../services/regions.service";
import {
  errorMsgGeomType,
  errorMsgNoFile,
  errorMsgShpFile,
  errorMsgVertices, titleCounty,
  titleDistrict, titleGemarkungenFluren, titleKommunen,
  titleState
} from "../../var/constants";
import {EventsService} from "../../services/events.service";
import {SettingsService} from "../../services/settings.service";
import {GeoJSONGeometry} from 'ol/format/GeoJSON';
import Polygon from "ol/geom/Polygon";
import {Feature} from "ol";
import {getArea} from 'ol/sphere';
import {MapService} from "../../services/map.service";

/**
 * Folgende Parser sollten ausprobiert werden, auf Grund der Proj Problematik
 * Shapefile Parser
 * https://www.npmjs.com/package/shapefile
 *
 * Convert shapefile to geojson
 * https://github.com/gipong/shp2geojson.js
 */

proj4.defs('EPSG:25832', '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
proj4.defs('EPSG:31466', '+proj=tmerc +lat_0=0 +lon_0=6 +k=1 +x_0=2500000 +y_0=0 +ellps=bessel ' +
  '+towgs84=598.1,73.7,418.2,0.202,0.045,-2.455,6.7 +units=m +no_defs');
proj4.defs('EPSG:31467', '+proj=tmerc +lat_0=0 +lon_0=9 +k=1 +x_0=3500000 +y_0=0 +ellps=bessel ' +
  '+towgs84=598.1,73.7,418.2,0.202,0.045,-2.455,6.7 +units=m +no_defs');


@Component({
  selector: 'app-area-selection',
  templateUrl: './area-selection.component.html',
  styleUrls: ['./area-selection.component.scss']
})
export class AreaSelectionComponent implements AfterViewInit {

  @ViewChild('help', {static: false}) help: TemplateRef<any> | undefined;
  @ViewChild('areaTooSmall', {static: false}) areaTooSmall: TemplateRef<any>| undefined;

  selectedProduct: string;

  checkstateStates: any;
  checkstateDistricts: any;
  checkstateCounties: any;
  checkstateKommunen: any;
  checkstateGemarkungen: any;

  listOfGemarkungen: any;

  searchText: string = '';
  activeNav: string;

  errorMsg: string;
  hideHelp: boolean = false;

  totalSelectionArea = 0;

  fileToUpload: File | undefined;
  fileType: 'shp' | 'zip' | undefined;
  selectedEPSG: string = 'EPSG:3857';

  tabs = {
    titleState: titleState,
    titleDistrict: titleDistrict,
    titleCounty: titleCounty,
    titleKommunen: titleKommunen
  }

  constructor(
    productService: ProductService,
    private events: EventsService,
    private mapService: MapService,
    private modalService: NgbModal,
    private settingsService: SettingsService,
    public regionsService: RegionsService
  ) {
    this.selectedProduct = productService.getSelectedProductId();

    // Initialize objects with the selected checboxstates
    this.checkstateStates = regionsService.getSelectedStates();
    this.checkstateDistricts = regionsService.getSelectedDistricts();
    this.checkstateCounties = regionsService.getSelectedCounties();
    this.checkstateKommunen = regionsService.getSelectedKommunen();
    this.checkstateGemarkungen = [];
    this.listOfGemarkungen = [];
    // Default: On page load the active tab is set to states
    // The first tab on startup is the states tab
    this.activeNav = titleState;
    this.errorMsg = '';
  }

  async ngAfterViewInit() {
    // restore value for hideHelp
    this.hideHelp = await this.settingsService.get('hideAreaSelectionHelp') === 'true';
    if (!this.hideHelp) {
      this.showAbout(this.help);
    }
    this.events.subscribe('total-area:change', (area) => this.totalSelectionArea = area / 1e6);
    this.events.subscribe('map-selection:too-small', () => this.showAbout(this.areaTooSmall));
    this.events.subscribe('user:logout', () => this.reset());

    this.events.subscribe('product-changed', () => this.reset());
  }

  /**
   * On nav change the id of the active nav is set. If the user selects a region
   * it is recommended to know whether it is a state or a district.
   * @param event on nav change event
   */
  onNavChange(event: NgbNavChangeEvent) {
    this.activeNav = event.nextId;
  }

  /**
   * Search for Gemarkungen and Fluren. A REST api from Geobasis NRW is used.
   * @param searchValue search string
   */
  searchBox(searchValue: string): void {
    const tmp: string[] = [];
    if (searchValue !== '' && this.activeNav === 'Gemarkungen') {
      // Geobasis NRW REST api
      this.regionsService.getFlurGemarkungen(searchValue).subscribe(value => {
        if (value.length > 0) {
          value[0].result.forEach((val: any) => {
            // Only if the propertie flur is available the record contains Flurstücke
            if (val.flur) {
              // tslint:disable-next-line: max-line-length
              tmp.push(val.gemeinde + ', Gemarkung ' + val.gemarkungsnr + ' (' + val.gemarkungsname + '), Flur ' + val.flur + ', Flurst. ' + val.zaehler);
            } else {
              tmp.push(val.gemeinde + ', Gemarkung ' + val.gemarkungsnr + ' (' + val.gemarkungsname + ')');
            }
          });
          // Set list of gemarkungen and fluren. The variable is read out with a ngFor inside the html component
          this.listOfGemarkungen = tmp;
        } else {
          this.listOfGemarkungen = tmp;
        }
      });
    }
  }

  /**
   * Decision is made, wheather the user wants to draw a rectangle or a polygon.
   * @param drawType area of interest
   */
  draw(drawType: string): void {
    this.mapService.setDrawInteraction(drawType);
    this.clearAllCheckboxes();
    this.mapService.clearMap('regionsLayer');
  }

  checkFile(event: any) {
    if (event.currentTarget.value) {
      const fileName = event.target.files[0].name;
      const fileType = fileName.split('.').slice(-1)[0];
      if (fileType === 'shp' || fileType === 'zip') {
        this.fileToUpload = event.target.files[0];
        this.fileType = fileType;
      } else {
        this.errorMsg = errorMsgShpFile;
      }
    } else {
      this.errorMsg = errorMsgNoFile;
    }

    // The error msg box will be closed after 5000 ms
    const showAlertInterval = setInterval(() => {
      this.errorMsg = '';
      clearInterval(showAlertInterval);
    }, 5000);
  }

  /**
   * The user can upload his own shp file. The geometry will be displayed on the map.
   * Previous several checks are carried out.
   * 1) The type of the geometry must be Polygon or Multipolygon.
   * 2) Number of vertices must be lower than 1000
   * 3) Only .shp files are allowed
   */
  uploadFile() {
    if (this.fileToUpload) {
      const reader = new FileReader();
      if (this.fileType === 'shp' && this.selectedEPSG) {
        reader.onload = (evt: any) => {
          // Parse shp into geojson
          // For further information: https://github.com/calvinmetcalf/shapefile-js
          const geometryArray = shp.parseShp(evt.target.result, undefined);
          this.processGeometries(geometryArray, this.fileToUpload!.name, this.selectedEPSG!);
        };
      } else {
        reader.onload = (evt: any) => {
          let featureCollection: any;
          featureCollection = shp.parseZip(evt.target.result);
          this.processGeometries([featureCollection.features[0].geometry], this.fileToUpload!.name, 'EPSG:4326');
        };
      }
      // Reading the contents of the Blob, the result contains an arraybuffer
      reader.readAsArrayBuffer(this.fileToUpload);

      // The error msg box will be closed after 5000 ms
      const showAlertInterval = setInterval(() => {
        this.errorMsg = '';
        clearInterval(showAlertInterval);
      }, 5000);
    }
  }

  processGeometries(geometries: GeoJSONGeometry[], filename: string, epsg: string) {
    const layer = this.mapService.getVectorLayer('drawLayer');
    const map = this.mapService.getMap();
    if (map) {
      // All selected regions will be deselected and the layer is cleared
      this.clearAllCheckboxes();
      this.mapService.clearMap();
      // Array can contain multiple geometries
      geometries.forEach(geometry => {
        // The type of the geometry must be Polygon or Multipolygon.
        if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
          // Number of vertices must be lower than 10000
          if (geometry.coordinates[0].length <= 10000) {
            const coordinates = geometry.coordinates;
            const tmpCoords: any[] = [];
            coordinates[0].forEach(val => {
              tmpCoords.push(proj4(epsg, 'EPSG:3857', val as TemplateCoordinates));
            });
            coordinates[0] = tmpCoords;
            // For each geometrie in the shp file a ol.feature is created
            const polygon = new Polygon(coordinates as number[][][]);
            const feature = new Feature({
              geometry: polygon,
              name: filename
            });
            this.events.publish('total-area:change', getArea(polygon));
            // Add the feature to the map
            layer.getSource()?.addFeature(feature);
            // Set view to the bbox of the added features
            map.getView().fit(feature.getGeometry()!.getExtent());
          } else {
            this.errorMsg = errorMsgVertices;
          }
        } else {
          this.errorMsg = errorMsgGeomType;
        }
      });
    }
  }

  /**
   * This function is triggered by the 'Delete geometry" button
   * @param layerName name of the layer to be clear
   */
  deleteGeom(layerName: string): void {
    this.mapService.clearMap(layerName);
    if (layerName === 'drawLayer') {
      this.fileToUpload = undefined;
      this.fileType = undefined;
      this.selectedEPSG = 'EPSG:3857';
    }
  }

  /**
   * All checkboxes will be reset to the initial state
   */
  clearAllCheckboxes() {
    // All selected administration units will be deselected
    this.regionsService.resetStates();
    // Two-way-data binding. So that the changes become visible, the variables must be set with the empty objects.
    this.checkstateStates = this.regionsService.getSelectedStates();
    this.checkstateDistricts = this.regionsService.getSelectedDistricts();
    this.checkstateCounties = this.regionsService.getSelectedCounties();
    this.checkstateKommunen = this.regionsService.getSelectedKommunen();
  }

  /**
   * It is possible to select multiple administration units inside of a tab.
   * It is not possible to select entries in multiple tabs.
   * If the user selects an entry inside tab A and afterwards an entry in tab B. The selected entry in tab A will be deselected.
   * This function is triggered on a checkchange event.
   */
  handleCheckboxStates(): void {
    // All selected administration units will be deselected
    this.regionsService.resetStates();
    // The selected tab is not equal to the state-tab. So, the variable will be reset. It is set with an empty object.
    if (this.activeNav !== titleState) {
      this.checkstateStates = this.regionsService.getSelectedStates();
    } else {
      // In this case, the selected tab is equal to the state-tab.
      // The variable is set with the selected entry.
      this.regionsService.setSelectedStates(this.checkstateStates);
      // The corresponding geometry/geometries is/are extraced from the geomdata and is displayed on the map.
      this.displaySelectedAdminUnit(this.regionsService.getTrueValues(this.checkstateStates));
    }

    // Below are the same if-else-blocks like before. Every if-else-block checks if the current tab belongs to district, county and so on.
    // The selected entry/entries will be set and the corresponding geometry/geometries is/are displayed on the map.

    if (this.activeNav !== titleDistrict) {
      this.checkstateDistricts = this.regionsService.getSelectedDistricts();
    } else {
      this.regionsService.setSelectedDistricts(this.checkstateDistricts);
      this.displaySelectedAdminUnit(this.regionsService.getTrueValues(this.checkstateDistricts));
    }

    if (this.activeNav !== titleCounty) {
      this.checkstateCounties = this.regionsService.getSelectedCounties();
    } else {
      this.regionsService.setSelectedCounties(this.checkstateCounties);
      this.displaySelectedAdminUnit(this.regionsService.getTrueValues(this.checkstateCounties));
    }

    if (this.activeNav !== titleKommunen) {
      this.checkstateKommunen = this.regionsService.getSelectedKommunen();
    } else {
      this.regionsService.setSelectedKommunen(this.checkstateKommunen);
      this.displaySelectedAdminUnit(this.regionsService.getTrueValues(this.checkstateKommunen));
    }

    if (this.activeNav !== titleGemarkungenFluren) {
      // this.checkstateKommunen = this.regionsService.getSele();
    } else {
      this.regionsService.setSelectedGemarkungen(this.checkstateGemarkungen);
      this.displaySelectedAdminUnit(this.regionsService.getTrueValues(this.checkstateGemarkungen));
    }
  }

  /**
   * Selected administration units are shown on the map
   * @param selectedRegions selected administration units
   */
  displaySelectedAdminUnit(selectedRegions: object): void {
    const layer = this.mapService.getVectorLayer('regionsLayer');

    const features = layer.getSource()?.getFeatures();
    features?.forEach((feature: any) => {
      layer.getSource()?.removeFeature(feature);
    });

    const keys = Object.values(selectedRegions);
    this.regionsService.clearAllKN();

    let totalSelectionArea = 0;
    keys.forEach(value => {
      // Get geom of selected unit
      const feature = this.regionsService.getGeomOfSelectedItem(this.activeNav, value);
      totalSelectionArea += getArea(feature.getGeometry() as Polygon);
      layer.getSource()?.addFeature(feature);
    });
    // this.totalSelectionArea = totalSelectionArea / 1e6;
    this.events.publish('total-area:change', totalSelectionArea);
  }

  /**
   * Change event is fired if a checkbox is checked or unchecked.
   * The corresponding geometry is displayed on the map.
   */
  checkChange(): void {
    this.mapService.clearMap('drawLayer');
    this.mapService.clearMap('regionsLayer');
    this.handleCheckboxStates();
  }

  /**
   * Opens the About in a modal component
   * @param aboutUs NgbModal element
   * @returns void
   */
  showAbout(aboutUs: any) {
    this.modalService.open(aboutUs, {size: 'lg'});
  }

  async toggleAreaSelectionHelp() {
    await this.settingsService.set('hideAreaSelectionHelp', this.hideHelp.toString());
  }

  selectEPSG(event: any) {
    this.selectedEPSG = event.target.selectedOptions[0].value;
  }

  private reset() {
    this.mapService.clearMap();
    this.clearAllCheckboxes();
    this.fileToUpload = undefined;
    this.fileType = undefined;
    this.selectedEPSG = 'EPSG:3857';
  }

  getFileName(): string {
    if (this.fileToUpload && this.fileToUpload.name) {
      return this.fileToUpload.name;
    } else {
      return '.shp/.zip';
    }
  }

  setSelectedState(bundesland: string) {
    // All selected administration units will be deselected
    this.regionsService.resetStates();

    // set selected state
    this.checkstateStates = {};
    this.checkstateStates[bundesland] = true;
    this.regionsService.setSelectedStates(this.checkstateStates);

    // The corresponding geometry/geometries is/are extraced from the geomdata and is displayed on the map.
    this.displaySelectedAdminUnit(this.regionsService.getTrueValues(this.checkstateStates));
  }
}
